import React from 'react';
import { AuthContext, AuthContextType } from 'contexts';
import styled from 'styled-components';
import { Country } from '@bb/domain/dist/types';
import { Icon as NewIcon } from '@brokerbay/elements-web';
import Dropdown from '../Dropdown';
import { Menu, MenuItemGroup, MenuItem, MenuDivider } from '../Menu';
import { StyledHeaderItem } from './StyledComponents';
import Icon from '../Icon';
import theme, { ColorNameType } from '../theme';
import { Text } from '../Typography';

const platformStatusLink = 'https://brokerbay.statuspage.io';

const iconColor: ColorNameType = 'gray8';

const getSupportLinks = (
  user: AuthContextType['auth'],
  portalConfig: AuthContextType['onboardingPortalConfig'],
) => {
  const {
    agentTrainingLink,
    adminTrainingLink,
    helpCenterLink,
    contactUsLink,
  } = portalConfig;

  return {
    agentTrainingLink,
    adminTrainingLink,
    helpCenterLink,
    contactUsLink,
  };
};

type SupportMenuProps = {
  isMobile: boolean;
  iconSizes: {
    width: number;
    height: number;
  };
  $height: number;
  showingsFeatureOn?: boolean;
};

const SupportMenu: React.FC<SupportMenuProps> = ({
  isMobile,
  iconSizes,
  $height,
  showingsFeatureOn,
}) => {
  const { auth, onboardingPortalConfig } = React.useContext(AuthContext);
  const [overlayVisible, setOverlayVisible] = React.useState(false);
  const anchorRef = React.useRef(null);

  const supportLinks = getSupportLinks(auth, onboardingPortalConfig);

  const hasTrainingLinks =
    supportLinks.agentTrainingLink || supportLinks.adminTrainingLink;
  const hasHelpLinks =
    supportLinks.helpCenterLink || supportLinks.contactUsLink;
  const showAdminTraining =
    auth.role !== 'AGENT' && supportLinks.adminTrainingLink;

  return (
    <div>
      <Dropdown
        trigger={['click']}
        open={overlayVisible}
        onOpenChange={visible => setOverlayVisible(visible)}
        getPopupContainer={() => anchorRef.current!}
        dropdownRender={() => (
          <Menu>
            <MenuItem
              key={'status'}
              href={platformStatusLink}
              target="_blank"
              onClick={() => setOverlayVisible(false)}
            >
              <SupportMenuItemText>Platform Status</SupportMenuItemText>
            </MenuItem>
            {hasTrainingLinks && <MenuDivider />}
            {hasTrainingLinks && (
              <MenuItemGroup
                title={
                  <MenuItemGroupTitleWrapper>
                    <MenuItemGroupTitle>Training Webinars</MenuItemGroupTitle>
                    <Icon
                      color={iconColor}
                      type="arrowDownThin"
                      width={9}
                      height={6}
                    />
                  </MenuItemGroupTitleWrapper>
                }
              >
                {showAdminTraining && (
                  <MenuItem
                    key={'admins'}
                    href={supportLinks.adminTrainingLink}
                    target="_blank"
                    onClick={() => setOverlayVisible(false)}
                  >
                    <IndentedSupportMenuItemText>
                      For Admins
                    </IndentedSupportMenuItemText>
                  </MenuItem>
                )}
                {supportLinks.agentTrainingLink && (
                  <MenuItem
                    key={'agents'}
                    href={supportLinks.agentTrainingLink}
                    target="_blank"
                    onClick={() => setOverlayVisible(false)}
                  >
                    <IndentedSupportMenuItemText>
                      For Agents
                    </IndentedSupportMenuItemText>
                  </MenuItem>
                )}
              </MenuItemGroup>
            )}
            {hasHelpLinks && <MenuDivider />}
            {supportLinks.helpCenterLink && (
              <MenuItem
                key={'help-center'}
                href={supportLinks.helpCenterLink}
                target="_blank"
                onClick={() => setOverlayVisible(false)}
              >
                <SupportMenuItemText>Help Center</SupportMenuItemText>
              </MenuItem>
            )}
            {supportLinks.contactUsLink && (
              <MenuItem
                key={'contact'}
                href={supportLinks.contactUsLink}
                target="_blank"
                onClick={() => setOverlayVisible(false)}
              >
                <SupportMenuItemText>Contact Us</SupportMenuItemText>
              </MenuItem>
            )}
          </Menu>
        )}
      >
        <StyledHeaderItem
          $height={$height}
          $isMobile={isMobile}
          $showBackgroundColor
          $hasShowingsMadeEasyFeature={showingsFeatureOn}
        >
          <button>
            {showingsFeatureOn ? (
              <NewIcon
                icon="questionCircleFilled"
                color={isMobile ? 'default' : 'white'}
                size={24}
              />
            ) : (
              <Icon type="questionMark" {...iconSizes} />
            )}
          </button>
          {isMobile && (
            <>
              <div>
                <Text type="secondary">Support</Text>
              </div>
              <div>
                <StyledArrowIcon
                  $overlayVisible={overlayVisible}
                  type="arrowDownThick"
                  width={8}
                />
              </div>
            </>
          )}
        </StyledHeaderItem>
      </Dropdown>
      <StyledAnchor $isMobile={isMobile} ref={anchorRef} />
    </div>
  );
};

const StyledAnchor = styled.div<{
  $isMobile: boolean;
}>`
  ${({ $isMobile }) => {
    if ($isMobile) {
      return `
        && {
          ${Menu} {
            box-shadow: ${theme.getElevation('bottom')};
            background-color: ${theme.gray1};
            border-radius: 0;
            position: relative;
            top: -6px;
            ${MenuItem} {
              padding-left: 25px;
            }
          }
        }
      `;
    }
    return '';
  }}
`;

const StyledArrowIcon = styled(Icon)<{ $overlayVisible: boolean }>`
  && {
    margin: 0 5px;
    ${({ $overlayVisible }) =>
      `transform: scaleY(${$overlayVisible ? -1 : 1});`}
    transition: all 1s;
  }
`;

const SupportMenuItemText = styled.span`
  color: ${theme.gray8};
`;

const IndentedSupportMenuItemText = styled(SupportMenuItemText)`
  padding-left: 18px;
`;

const MenuItemGroupTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  color: ${theme.gray8};
`;

const MenuItemGroupTitle = styled.div`
  margin-right: 30px;
`;

export default SupportMenu;
